import React, { useRef, useMemo, useEffect, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ShieldCheck } from "lucide-react";

const slideInFromTop = {
  hidden: { y: -100, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { delay: 0.5, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

const KEYFRAMES = `
@keyframes scrollBgSlow {
  0% { transform: translateY(0); }
  100% { transform: translateY(-50%); }
}
@keyframes scrollBgFast {
  0% { transform: translateY(-50%); }
  100% { transform: translateY(0); }
}
@keyframes pulseGlow {
  0%, 100% { opacity: 0.15; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.25; transform: translate(-50%, -50%) scale(1.1); }
}
`;

const Security = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });

  // Generate background text layers
  const bgRows = useMemo(() => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=$@#";
    const generateRows = (count, len) => Array.from({ length: count }).map(() =>
      Array.from({ length: len }).map(() => chars[Math.floor(Math.random() * chars.length)]).join("")
    );
    return {
      layer1: generateRows(30, 120),
      layer2: generateRows(20, 100)
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative flex flex-col items-center justify-center min-h-screen w-full overflow-hidden bg-[var(--ink)]"
      id="security"
    >
      <style>{KEYFRAMES}</style>

      {/* ─── BACKGROUND LAYERS ─── */}
      <div className="absolute inset-0 z-10 pointer-events-none select-none overflow-hidden">
        {/* Layer 1: Slow background scrolling */}
        <div 
          className="absolute inset-0 w-full h-[200%] opacity-[0.12] flex flex-col gap-2"
          style={{
            animation: "scrollBgSlow 60s linear infinite",
            maskImage: "radial-gradient(circle at center, transparent 0%, black 60%, transparent 100%)",
            WebkitMaskImage: "radial-gradient(circle at center, transparent 0%, black 60%, transparent 100%)",
          }}
        >
          {[...bgRows.layer1, ...bgRows.layer1].map((row, i) => (
            <FlickerRow key={`l1-${i}`} text={row} />
          ))}
        </div>

        {/* Layer 2: Fast foreground scrolling */}
        <div 
          className="absolute inset-0 w-full h-[200%] opacity-[0.08] flex flex-col gap-2"
          style={{
            animation: "scrollBgFast 40s linear infinite",
            maskImage: "radial-gradient(circle at center, transparent 10%, black 70%, transparent 100%)",
            WebkitMaskImage: "radial-gradient(circle at center, transparent 10%, black 70%, transparent 100%)",
          }}
        >
          {[...bgRows.layer2, ...bgRows.layer2].map((row, i) => (
            <FlickerRow key={`l2-${i}`} text={row} />
          ))}
        </div>
        
        {/* Vignette Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--ink)] via-transparent to-[var(--ink)] opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)] opacity-90" />
      </div>

      {/* Pulsing Central Glow */}
      <div className="absolute top-1/2 left-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(228,199,122,0.1) 0%, transparent 70%)",
          filter: "blur(80px)",
          animation: "pulseGlow 8s ease-in-out infinite"
        }}
      />

      {/* ─── CONTENT ─── */}
      <div className="container-xl relative z-30 flex flex-col items-center">
        {/* Heading */}
        <motion.div
          variants={slideInFromTop}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="mb-12 md:mb-20"
        >
          <h2 className="font-display text-[clamp(42px,8vw,92px)] font-medium text-center text-[var(--cream)] leading-[1.05]">
            Performance <span className="gold-text italic font-display">&</span> security.
          </h2>
        </motion.div>

        {/* Interactive Lock Area */}
        <div className="relative flex flex-col items-center group cursor-pointer">
          <div className="relative h-48 flex flex-col items-center justify-center">
            {/* Shackle (Top Part) */}
            <motion.div
              initial={{ y: 25 }}
              animate={{ y: 25 }}
              whileHover={{ y: 8 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              className="relative z-20"
            >
              <svg width="60" height="50" viewBox="0 0 24 16" fill="none">
                <path d="M6 16V7C6 3.68629 8.68629 1 12 1C15.3137 1 18 3.68629 18 7V16" stroke="#e4c77a" strokeWidth="1.8" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 8px rgba(228,199,122,0.4))" }}/>
              </svg>
            </motion.div>

            {/* Lock Body */}
            <div className="relative z-10 -mt-2">
              <svg width="84" height="72" viewBox="0 0 24 20" fill="none">
                <rect x="3" y="1" width="18" height="18" rx="3" stroke="#e4c77a" strokeWidth="1.8" fill="rgba(20,17,15,0.9)" style={{ filter: "drop-shadow(0 0 15px rgba(228,199,122,0.1))" }}/>
                <circle cx="12" cy="10" r="2" fill="#e4c77a"/>
                <path d="M12 12V15" stroke="#e4c77a" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>

            {/* Circular Orbit Ring */}
            <motion.div 
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 w-64 h-64 border border-dashed border-[rgba(228,199,122,0.15)] rounded-full -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"
            />
          </div>

          {/* Encryption Badge */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.8 }}
            className="px-6 py-2 mt-8 rounded-sm border backdrop-blur-xl flex items-center gap-3"
            style={{
              borderColor: "rgba(228,199,122,0.3)",
              background: "linear-gradient(to right, rgba(20,17,15,0.8), rgba(228,199,122,0.05))",
              boxShadow: "0 10px 30px rgba(0,0,0,0.4)"
            }}
          >
            <ShieldCheck size={14} className="text-[var(--gold)]" />
            <span className="font-impact text-[13px] tracking-[0.35em] uppercase text-[var(--gold)]">Encryption</span>
          </motion.div>
        </div>

        {/* Descriptive Text */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-16 md:mt-24 font-display italic text-[clamp(18px,2.5vw,26px)] text-[var(--cream-muted)] text-center max-w-2xl leading-relaxed balance"
        >
          “Security isn't a feature, it's a foundation. We build ironclad digital studios where your data is <span className="gold-text not-italic">yours alone</span>.”
        </motion.p>
      </div>
    </section>
  );
};

/* ── Individual line with live character glitching ── */
const FlickerRow = ({ text }) => {
  const [display, setDisplay] = useState(text);

  useEffect(() => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=$@#";
    const iv = setInterval(() => {
      setDisplay(prev => 
        prev.split("").map((c, i) => Math.random() > 0.96 ? chars[Math.floor(Math.random() * chars.length)] : text[i]).join("")
      );
    }, 150);
    return () => clearInterval(iv);
  }, [text]);

  return (
    <div className="whitespace-nowrap font-mono text-[9px] tracking-[0.6em] text-[var(--gold)] text-center w-full">
      {display}
    </div>
  );
};

export default Security;
